import React, { useState, useEffect } from 'react';
import { INCIDENTS_API } from '@/lib/incidentsApi';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import EnhancedTicketDetail from './EnhancedTicketDetail';
import ErrorTicketDetail from './ErrorTicketDetail';
import SapJobFailureTicketDetail from './SapJobFailureTicketDetail';
import WorkflowTicketDetail from './WorkflowTicketDetail';
import { isWorkflowTicket } from '@/lib/workflowHelpers';
import { Loader2 } from 'lucide-react';

const API = INCIDENTS_API;
const WORKFLOW_CLASSIFICATION_POLL_MS = 3000;
const MAX_WORKFLOW_CLASSIFICATION_POLLS = 40;

function mayBecomeWorkflowTicket(ticket) {
  const ticketType = ticket?.customization_type || ticket?.category || '';
  return ticketType.trim().toLowerCase() === 'security';
}

export default function TicketDetailRouter() {
  const { id } = useParams();
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    let pollTimer;
    let pollCount = 0;

    const fetchTicket = async () => {
      let nextTicket = null;
      try {
        // id from useParams is ticket_number
        const response = await axios.get(`${API}/tickets/${id}`);
        nextTicket = response.data;
        if (!cancelled) {
          setTicket(nextTicket);
        }
      } catch (error) {
        console.error('Error fetching ticket:', error);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }

      // Security tickets are classified asynchronously by the Security
      // Orchestrator. Keep checking briefly so a ticket opened immediately
      // after creation can transition from the generic detail to workflow UI.
      if (
        !cancelled &&
        mayBecomeWorkflowTicket(nextTicket) &&
        !nextTicket.is_error_ticket &&
        !isWorkflowTicket(nextTicket) &&
        pollCount < MAX_WORKFLOW_CLASSIFICATION_POLLS
      ) {
        pollCount += 1;
        pollTimer = window.setTimeout(fetchTicket, WORKFLOW_CLASSIFICATION_POLL_MS);
      }
    };

    fetchTicket();

    return () => {
      cancelled = true;
      window.clearTimeout(pollTimer);
    };
  }, [id]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (!ticket) {
    return <div className="flex items-center justify-center h-full">Ticket not found</div>;
  }

  // Route to specialized view based on ticket type
  if (ticket.customization_type === 'sap_job_failure') {
    return <SapJobFailureTicketDetail />;
  } else if (ticket.is_error_ticket) {
    return <ErrorTicketDetail />;
  } else if (isWorkflowTicket(ticket)) {
    return <WorkflowTicketDetail />;
  } else {
    return <EnhancedTicketDetail />;
  }
}
